import { createSlice } from '@reduxjs/toolkit';
import { createUserAsync, getUsersAsync, getUserByIdAsync, updateUserAsync, getUserRolesAsync } from './usersActions';

const initialState = {
  users: [], staffList: [], managerList: [], managerRole: null,
  isLoading: false, pendingLists: 0, error: null, successMessage: null,
  registry: { loading: false, error: null, requestId: null },
  profiles: {}, roles: [], rolesLoading: false, rolesError: null, rolesRequestId: null,
  saving: false, saveError: null,
};
const unpack = payload => payload.user || payload.data || payload;
function upsert(list, user) {
  const index = list.findIndex(item => item.id === user.id);
  if (index === -1) list.push(user); else list[index] = user;
}
function rememberUser(state, user, addToLists = true) {
  state.profiles[user.id] = { ...state.profiles[user.id], data: user, error: null };
  if (!addToLists) return;
  upsert(state.users, user);
  const role = user.role?.name?.toUpperCase();
  state.staffList = state.staffList.filter(item => item.id !== user.id);
  state.managerList = state.managerList.filter(item => item.id !== user.id);
  if (role === 'STAFF') state.staffList.push(user);
  if (state.managerRole ? role === state.managerRole : ['ADMIN', 'MANAGER'].includes(role)) state.managerList.push(user);
}
const usersSlice = createSlice({
  name: 'users', initialState,
  reducers: {
    clearUserStatus(state) { state.error = null; state.successMessage = null; state.saveError = null; },
  },
  extraReducers: builder => {
    builder
      .addCase(getUsersAsync.pending, (state, action) => {
        state.pendingLists++; state.isLoading = true; state.error = null;
        if (!action.meta.arg) state.registry = { loading: true, error: null, requestId: action.meta.requestId };
      })
      .addCase(getUsersAsync.fulfilled, (state, action) => {
        state.pendingLists = Math.max(0, state.pendingLists - 1); state.isLoading = state.pendingLists > 0;
        const users = action.payload.data || action.payload;
        const role = action.meta.arg?.toUpperCase();
        if (role === 'STAFF') state.staffList = users;
        else if (['ADMIN', 'MANAGER'].includes(role)) { state.managerList = users; state.managerRole = role; }
        else if (state.registry.requestId === action.meta.requestId) {
          state.users = users; state.registry.loading = false; state.registry.error = null;
        }
      })
      .addCase(getUsersAsync.rejected, (state, action) => {
        state.pendingLists = Math.max(0, state.pendingLists - 1); state.isLoading = state.pendingLists > 0;
        if (!action.meta.aborted) state.error = action.payload || action.error.message;
        if (!action.meta.arg && state.registry.requestId === action.meta.requestId) {
          state.registry.loading = false; state.registry.error = action.meta.aborted ? null : state.error;
        }
      })
      .addCase(getUserByIdAsync.pending, (state, action) => {
        const id = action.meta.arg;
        state.profiles[id] = { ...state.profiles[id], loading: true, error: null, requestId: action.meta.requestId };
      })
      .addCase(getUserByIdAsync.fulfilled, (state, action) => {
        if (state.profiles[action.meta.arg]?.requestId !== action.meta.requestId) return;
        rememberUser(state, unpack(action.payload), false);
        state.profiles[action.meta.arg].loading = false;
      })
      .addCase(getUserByIdAsync.rejected, (state, action) => {
        const profile = state.profiles[action.meta.arg];
        if (profile?.requestId !== action.meta.requestId) return;
        profile.loading = false; profile.error = action.meta.aborted ? null : action.payload || action.error.message;
      })
      .addCase(getUserRolesAsync.pending, (state, action) => {
        state.rolesLoading = true; state.rolesError = null; state.rolesRequestId = action.meta.requestId;
      })
      .addCase(getUserRolesAsync.fulfilled, (state, action) => {
        if (state.rolesRequestId !== action.meta.requestId) return;
        state.rolesLoading = false; state.roles = action.payload.data || action.payload;
      })
      .addCase(getUserRolesAsync.rejected, (state, action) => {
        if (state.rolesRequestId !== action.meta.requestId) return;
        state.rolesLoading = false; state.rolesError = action.meta.aborted ? null : action.payload || action.error.message;
      });
    for (const thunk of [createUserAsync, updateUserAsync]) {
      builder.addCase(thunk.pending, state => { state.saving = true; state.saveError = null; state.successMessage = null; })
        .addCase(thunk.fulfilled, (state, action) => {
          state.saving = false; rememberUser(state, unpack(action.payload));
          state.successMessage = thunk === createUserAsync ? 'User created successfully.' : 'User updated successfully.';
        })
        .addCase(thunk.rejected, (state, action) => {
          state.saving = false; state.saveError = action.payload || action.error.message;
        });
    }
  },
});
export const { clearUserStatus } = usersSlice.actions;
export const selectAllUsers = state => state.users.users;
export const selectStaffList = state => state.users.staffList;
export const selectManagerList = state => state.users.managerList;
export const selectUsersLoading = state => state.users.isLoading;
export const selectUsersError = state => state.users.error;
export const selectCanManageUsers = state => {
  const role = state.auth.user?.role;
  return (typeof role === 'string' ? role : role?.name)?.toUpperCase() === 'ADMIN';
};
export default usersSlice.reducer;
